import { computed, ref, Ref } from "@vue/composition-api";
import { UploadFileData } from "@scrile/tools/dist/lib/FileUploader";
import useAuthUser from "@/hooks/useAuthUser";
import providers from "@/lib/api";
import { getFileMimeType } from "@/lib/fileHelpers";
import FileUploader, { calculateProgress, showDefaultUploadError } from "@/lib/FileUploader";
import { UploadVaultFile } from "@/types";

export const useVaultFiles = (selectedCategoryId: Ref<string>) => {
  const { authUserStrict } = useAuthUser();

  const moveLoading = ref(false);
  const deleteLoading = ref(false);
  const filesToUploadCount = ref(0);

  const uploadingFiles = computed(() => filesToUploadCount.value > 0);

  const moveFiles = async (fileIds: string[], categoryId: string) => {
    try {
      moveLoading.value = true;
      return await providers.VaultsProvider.vaultFilesMove({
        data: { userId: authUserStrict.value.id, ids: fileIds, destCategoryId: categoryId },
      });
    } finally {
      moveLoading.value = false;
    }
  };

  const deleteFiles = async (fileIds: string[]): Promise<boolean> => {
    try {
      deleteLoading.value = true;
      return await providers.VaultsProvider.deleteFileFromCategory({
        data: { ids: fileIds, categoryId: selectedCategoryId.value },
      });
    } finally {
      deleteLoading.value = false;
    }
  };

  const addFiles = async (
    files: FileList,
    onSuccess: (file: UploadVaultFile) => void,
    onUploadProgress: (file: UploadVaultFile, uploadProgress: number) => void
  ) => {
    if (!selectedCategoryId.value) return;
    const filesToUpload: UploadFileData[] = [];
    const uploadVaultFiles: UploadVaultFile[] = [];
    const uploadUrls = await providers.VaultsProvider.getUploadUrls(files.length);

    for (const [index, file] of Array.from(files).entries()) {
      const vaultFile: UploadVaultFile = {
        id: uploadUrls[index].uuid,
        userId: authUserStrict.value.id,
        uuid: uploadUrls[index].uuid,
        contentType: getFileMimeType(file),
        categoryIds: [selectedCategoryId.value],
        urlPart: "",
        createTime: "",
        lastUseTime: "",
        metadata: {},
        uploadProgress: 0,
      };
      uploadVaultFiles.push(vaultFile);
      filesToUpload.push({
        file,
        fileType: getFileMimeType(file),
        urlPart: uploadUrls[index].urlPart,
        id: `${uploadUrls[index].uuid}`,
        onSuccess: async () => {
          providers.VaultsProvider.createFile({
            data: {
              userId: authUserStrict.value.id,
              uuid: uploadUrls[index].uuid,
              categoryId: selectedCategoryId.value,
            },
          }).then(vaultFile => onSuccess(vaultFile));
          filesToUploadCount.value -= 1;
        },
        onUploadProgress: p => {
          onUploadProgress(vaultFile, calculateProgress(p));
        },
        onError: () => {
          showDefaultUploadError();
          filesToUploadCount.value -= 1;
        },
      });
    }
    filesToUploadCount.value += filesToUpload.length;
    FileUploader.addToQueue(filesToUpload);
    return uploadVaultFiles;
  };

  return {
    moveLoading,
    deleteLoading,
    uploadingFiles,
    moveFiles,
    deleteFiles,
    addFiles,
  };
};
